import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import { MenuPlacementProvider } from 'shared/components/ui/menuPlacement';

/**
 * The one dialog every dataset pop-up wears (Save, Import, Derived column,
 * Tags): the New eval chrome, compact. Title and a close button on top, the
 * body, then a footer with one ember primary and a neutral secondary.
 * Escape and a click outside close it.
 *
 * Menus opened inside (dropdowns, pickers) are portaled out of the card and
 * open upward, so opening one never grows or scrolls the card. `bodyClassName`
 * lets a dialog hold a steady height while its content changes.
 */
export const labelClass = 'text-[12px] font-medium text-[color:var(--text-secondary)]';
export const inputClass = 'h-8 w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 text-[12px] text-[color:var(--text-primary)] outline-none transition-colors placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)]';
export const textareaClass = 'w-full resize-none rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-2 text-[12px] leading-relaxed text-[color:var(--text-primary)] outline-none transition-colors placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)]';
export const primaryClass = 'inline-flex h-8 items-center justify-center rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50';
export const neutralClass = `${activityNeutralButtonClass} !px-3`;

export function Field({ label, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export default function DatasetDialog({ title, onClose, children, footer, width = 'max-w-[420px]', onSubmit, bodyClassName = '' }) {
  const panel = useRef(null);
  useEffect(() => {
    // An open menu takes the Escape first (it marks the event handled); only a bare one closes the dialog.
    const onKey = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const Body = onSubmit ? 'form' : 'div';
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh] backdrop-blur-sm"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-label={title}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Body ref={panel} {...(onSubmit ? { onSubmit: (e) => { e.preventDefault(); onSubmit(); } } : {})}
        className={`flex max-h-[80vh] w-full ${width} flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl`}
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">{title}</h2>
          <div className="flex-1" />
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>
        <div className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3 ${bodyClassName}`}>
          <MenuPlacementProvider value="top">{children}</MenuPlacementProvider>
        </div>
        {footer ? <div className="flex items-center justify-end gap-2 border-t border-[color:var(--border-subtle)] px-4 py-3">{footer}</div> : null}
      </Body>
    </div>,
    document.body,
  );
}
